import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, MessageSquareText, UserPlus, KeyRound, Trash2, ShieldCheck, ImagePlus, Wallet } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { fmtDate } from "@/lib/utils";
import BizPhotoManager from "@/components/BizPhotoManager";

export default function AdminSettings() {
  const { t } = useLang();
  const { user } = useAuth();
  const privileged = !!user?.is_main || !!user?.is_master;
  const myMobile = user?.mobile;
  const [tab, setTab] = useState("notifications");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: "", mobile: "", pin: "", admin_type: "admin" });
  const [bizPub, setBizPub] = useState(null);
  const [manualNotification, setManualNotification] = useState({ title: "", message: "" });

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((r) => setForm(r.data))
      .catch((e) => toast.error(fmtErr(e)));
  }, []);

  const loadAdmins = () => {
    if (!privileged) return;
    api
      .get("/admin/admins")
      .then((r) => setAdmins(r.data))
      .catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => {
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (privileged) return;
    api.get("/public/business").then((r) => setBizPub(r.data)).catch(() => {});
  }, [privileged]);

  const [qrUploading, setQrUploading] = useState(false);
  const uploadQr = async (file) => {
    if (!file) return;
    setQrUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/master/photos?kind=qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, payment_qr_url: data.payment_qr_url });
      toast.success(t("savedOk"));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setQrUploading(false);
    }
  };
  const deleteQr = async () => {
    if (!window.confirm(t("confirmDeletePhoto"))) return;
    try {
      await api.delete("/master/photos/qr/0");
      setForm({ ...form, payment_qr_url: null });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      toast.success("Notification settings saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sendManualNotification = async () => {
    if (!manualNotification.title.trim()) return toast.error("Enter a notification title");
    if (!manualNotification.message.trim()) return toast.error("Enter a notification message");

    setBusy(true);
    try {
      await api.post("/admin/notifications/send", manualNotification);
      toast.success("Notification sent successfully");
      setManualNotification({ title: "", message: "" });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const addAdmin = async () => {
    if (!newAdmin.name.trim()) return toast.error("Enter the admin's name");
    if (newAdmin.mobile.length !== 10) return toast.error("Enter a valid 10-digit mobile number");
    if (!/^\d{6}$/.test(newAdmin.pin)) return toast.error("PIN must be exactly 6 digits");
    setBusy(true);
    try {
      await api.post("/admin/admins", newAdmin);
      toast.success(`Admin added. ${newAdmin.name} can now login with OTP + PIN.`);
      setNewAdmin({ name: "", mobile: "", pin: "", admin_type: "admin" });
      loadAdmins();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPin = async (a) => {
    const pin = window.prompt(`Enter the new 6-digit PIN for ${a.name} (+91 ${a.mobile}):`);
    if (pin === null) return;
    if (!/^\d{6}$/.test(pin)) return toast.error("PIN must be exactly 6 digits");
    try {
      await api.put(`/admin/admins/${a.id}/pin`, { pin });
      toast.success(`PIN reset for ${a.name}`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Remove ${a.name} (+91 ${a.mobile}) as admin? They will no longer be able to login.`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success(`${a.name} removed from admins`);
      loadAdmins();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!form) return null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-settings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("settingsTitle")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("settingsSub")}</p>
        </div>
        {privileged && (
          <div className="flex gap-2">
            {[
              ["notifications", t("notifications")],
              ["admins", t("adminAccounts")],
            ].map(([key, label]) => (
              <button
                key={key}
                data-testid={`settings-tab-${key}`}
                onClick={() => setTab(key)}
                className={`text-xs font-bold px-3.5 py-1.5 rounded-full border transition-colors ${
                  tab === key ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {user?.is_master && (
        <div className="sf-card p-6 space-y-4">
          <div>
            <h2 className="font-display font-bold text-slate-900">
              Send Notification
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Send an announcement through Firebase and the portal notification panel.
            </p>
          </div>

          <input
            className="sf-input w-full"
            placeholder="Notification title"
            value={manualNotification.title}
            onChange={(e) =>
              setManualNotification({
                ...manualNotification,
                title: e.target.value,
              })
            }
          />

          <textarea
            className="sf-input w-full min-h-[100px]"
            placeholder="Notification message"
            value={manualNotification.message}
            onChange={(e) =>
              setManualNotification({
                ...manualNotification,
                message: e.target.value,
              })
            }
          />

          <button
            className="sf-btn-primary"
            onClick={sendManualNotification}
            disabled={busy}
          >
            {busy ? "Sending..." : "Send Notification"}
          </button>
        </div>
      )}

      {!privileged ? (
        <>
        <div className="sf-card p-6 space-y-4" data-testid="settings-readonly">
          <p className="text-base font-semibold text-slate-800">{t("masterOnlyNote")}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="sf-label">{t("adminMobile1")}</div>
              <div className="font-mono-sf font-bold text-slate-900">+91 {form.admin_mobile_1 || "—"}</div>
            </div>
            <div>
              <div className="sf-label">{t("adminMobile2")}</div>
              <div className="font-mono-sf font-bold text-slate-900">+91 {form.admin_mobile_2 || "—"}</div>
            </div>
            <div>
              <div className="sf-label">{t("upiIdLabel")}</div>
              <div className="font-mono-sf font-bold text-slate-900">{form.upi_id || "—"}</div>
            </div>
            <div>
              <div className="sf-label">{t("businessNameLabel")}</div>
              <div className="font-bold text-slate-900">{form.business_name || "—"}</div>
            </div>
          </div>
        </div>
        <div className="sf-card p-6 space-y-4 mt-6" data-testid="admin-photos-section">
          <div className="flex items-center gap-2 font-display font-bold text-slate-900">
            <ImagePlus className="h-4 w-4" /> {t("photosAdminTitle")}
          </div>
          <p className="text-sm text-slate-500">{t("photosAdminSub")}</p>
          {bizPub && <BizPhotoManager biz={bizPub} onChange={setBizPub} allowLogo={false} />}
        </div>
        </>
      ) : tab === "notifications" ? (
        <div className="sf-card p-6 space-y-4">
          <div className="flex items-center gap-2 font-display font-bold text-slate-900">
            <BellRing className="h-4 w-4" /> {t("adminAlertNumbers")}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="sf-label">{t("adminMobile1")}</label>
              <input data-testid="admin-mobile-1-input" className="sf-input" value={form.admin_mobile_1 || ""} onChange={(e) => setForm({ ...form, admin_mobile_1: e.target.value })} placeholder="98XXXXXXXX" />
            </div>
            <div>
              <label className="sf-label">{t("adminMobile2")}</label>
              <input data-testid="admin-mobile-2-input" className="sf-input" value={form.admin_mobile_2 || ""} onChange={(e) => setForm({ ...form, admin_mobile_2: e.target.value })} placeholder="98XXXXXXXX" />
            </div>
          </div>
          <div className="rounded-lg border-2 border-slate-200 p-5 space-y-4" data-testid="payment-settings-card">
            <div className="flex items-center gap-2 font-display font-bold text-slate-900">
              <Wallet className="h-4 w-4" /> {t("paymentSettings")}
            </div>
            <p className="text-xs text-slate-500 -mt-2">{t("paymentSettingsNote")}</p>
            <div>
              <label className="sf-label">{t("upiIdLabel")}</label>
              <input data-testid="upi-id-input" className="sf-input font-mono-sf" value={form.upi_id || ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="business@upi" />
            </div>
            <div className="flex items-start gap-5">
              <div className="shrink-0">
                <div className="sf-label">{t("paymentQrLabel")}</div>
                {form.payment_qr_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.payment_qr_url}`} alt="Payment QR" className="h-28 w-28 object-contain rounded-lg border border-slate-200 bg-white p-1" data-testid="payment-qr-preview" />
                ) : (
                  <div className="h-28 w-28 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-xs font-bold text-slate-400 text-center px-2">NO QR</div>
                )}
              </div>
              <div className="flex flex-col gap-2 pt-7">
                <label className="sf-btn-outline !py-2 !px-3 !text-xs cursor-pointer text-center">
                  <ImagePlus className="h-4 w-4 inline mr-1" /> {qrUploading ? t("uploading") : t("uploadQr")}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="payment-qr-upload-input" onChange={(e) => uploadQr(e.target.files?.[0])} />
                </label>
                {form.payment_qr_url && (
                  <button data-testid="delete-payment-qr-btn" className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 transition-colors" onClick={deleteQr}>
                    <Trash2 className="h-3 w-3" /> {t("deletePhoto")}
                  </button>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="sf-label">{t("bankAccountNumber")}</label>
                <input data-testid="bank-account-input" className="sf-input font-mono-sf" value={form.bank_account_number || ""} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} />
              </div>
              <div>
                <label className="sf-label">{t("bankName")}</label>
                <input data-testid="bank-name-input" className="sf-input" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
              </div>
              <div>
                <label className="sf-label">{t("bankHolderName")}</label>
                <input data-testid="bank-holder-input" className="sf-input" value={form.bank_holder_name || ""} onChange={(e) => setForm({ ...form, bank_holder_name: e.target.value })} />
              </div>
            </div>
          </div>
          <div>
            <label className="sf-label">{t("businessNameLabel")}</label>
            <input data-testid="business-name-input" className="sf-input" value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
          </div>
          <div className="rounded-lg border-2 border-slate-200 p-5 space-y-5" data-testid="invoice-pdf-settings-card">
            <div>
              <div className="font-display font-bold text-slate-900">Invoice / PDF Settings</div>
              <p className="text-xs text-slate-500 mt-1">
                Main Admin controls what information appears on future invoices and PDF receipts.
              </p>
            </div>

            <div>
              <div className="text-sm font-bold text-slate-800 mb-3">PDF Visibility</div>
              <div className="mb-4">
                <label className="sf-label">Mobile Number(s) to Show on PDF</label>
                <input
                  className="sf-input font-mono-sf"
                  value={form.pdf_mobile_numbers || ""}
                  onChange={(e) => setForm({ ...form, pdf_mobile_numbers: e.target.value })}
                  placeholder="e.g. 7865014730, 9933348949"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter only the mobile number(s) you want printed on invoices. Separate multiple numbers with commas.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ["pdf_show_business_address", "Show Business Address"],
                  ["pdf_show_license_no", "Show Fertilizer Licence No."],
                  ["pdf_show_gstin", "Show GSTIN"],
                  ["pdf_show_mobile", "Show Mobile Number"],
                  ["pdf_show_bank_details", "Show Bank Details"],
                  ["pdf_show_upi_id", "Show UPI ID"],
                  ["pdf_show_qr", "Show Payment QR"],
                  ["pdf_show_stamp", "Show Stamp"],
                  ["pdf_show_signature", "Show Signature"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer">
                    <span className="text-sm font-semibold text-slate-700">{label}</span>
                    <input
                      type="checkbox"
                      checked={form[key] !== false}
                      onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                      className="h-4 w-4"
                    />
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-bold text-slate-800 mb-3">Payment Details</div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="sf-label">UPI Display Name</label>
                  <input
                    className="sf-input"
                    value={form.upi_display_name || ""}
                    onChange={(e) => setForm({ ...form, upi_display_name: e.target.value })}
                    placeholder="Business / Account Name"
                  />
                </div>
                <div>
                  <label className="sf-label">Bank IFSC</label>
                  <input
                    className="sf-input font-mono-sf"
                    value={form.bank_ifsc || ""}
                    onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })}
                    placeholder="IFSC CODE"
                  />
                </div>
                <div>
                  <label className="sf-label">Bank Branch</label>
                  <input
                    className="sf-input"
                    value={form.bank_branch || ""}
                    onChange={(e) => setForm({ ...form, bank_branch: e.target.value })}
                    placeholder="Branch"
                  />
                </div>
              </div>
            </div>

            {privileged && (
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-5 space-y-3" data-testid="annual-return-template-settings">
                <div>
                  <div className="font-display font-bold text-slate-900">Government Annual Fertilizer Return Template</div>
                  <p className="text-xs text-slate-600 mt-1">
                    Upload the latest official government PDF template. When the government issues a new template, upload the replacement here.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <div className="text-sm font-semibold text-slate-800">
                      {form.annual_return_template_name || "No government template uploaded"}
                    </div>
                    {form.annual_return_template_updated_at && (
                      <div className="text-xs text-slate-500 mt-1">
                        Last updated: {fmtDate(form.annual_return_template_updated_at)}
                      </div>
                    )}
                  </div>

                  <label className="sf-btn-outline !py-2.5 !px-4 cursor-pointer text-center">
                    Upload / Replace PDF
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const { data } = await api.post("/master/annual-return-template", fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                          });
                          setForm({
                            ...form,
                            annual_return_template_url: data.annual_return_template_url,
                            annual_return_template_name: data.annual_return_template_name,
                            annual_return_template_updated_at: data.annual_return_template_updated_at,
                          });
                          toast.success("Government annual return template uploaded");
                        } catch (err) {
                          toast.error(fmtErr(err));
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
            )}

            {privileged && (
              <div className="rounded-lg border-2 border-blue-200 bg-blue-50/40 p-5 space-y-3" data-testid="opening-animation-settings">
                <div>
                  <div className="font-display font-bold text-slate-900">Sabuj Sona Opening Animation</div>
                  <p className="text-xs text-slate-600 mt-1">
                    Upload the professional splash background shown when the Sabuj Sona application opens. The same responsive design can be used by the website and Android app.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <div className="text-sm font-semibold text-slate-800">
                      {form.opening_animation_name || "No splash image uploaded"}
                    </div>
                    {form.opening_animation_updated_at && (
                      <div className="text-xs text-slate-500 mt-1">
                        Last updated: {fmtDate(form.opening_animation_updated_at)}
                      </div>
                    )}
                  </div>

                  <label className="sf-btn-outline !py-2.5 !px-4 cursor-pointer text-center">
                    Upload / Replace Splash Image
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const { data } = await api.post("/master/opening-animation", fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                          });
                          setForm({
                            ...form,
                            opening_animation_url: data.opening_animation_url,
                            opening_animation_name: data.opening_animation_name,
                            opening_animation_updated_at: data.opening_animation_updated_at,
                          });
                          toast.success("Splash image uploaded");
                        } catch (err) {
                          toast.error(fmtErr(err));
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["invoice_stamp_url", "Invoice Stamp", "stamp", "Upload Stamp"],
                ["invoice_signature_url", "Authorized Signature", "signature", "Upload Signature"],
              ].map(([field, label, kind, actionLabel]) => (
                <div key={field}>
                  <label className="sf-label">{label}</label>
                  <div className="flex items-center gap-3">
                    {form[field] ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${form[field]}`}
                        alt={label}
                        className="h-20 w-32 object-contain rounded-lg border border-slate-200 bg-white p-2"
                      />
                    ) : (
                      <div className="h-20 w-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-xs font-bold text-slate-400 text-center px-2">
                        NO {label.toUpperCase()}
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <label className="sf-btn-outline !py-2 !px-3 !text-xs cursor-pointer text-center">
                        <ImagePlus className="h-4 w-4 inline mr-1" />
                        {actionLabel}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const fd = new FormData();
                              fd.append("file", file);
                              const { data } = await api.post(`/master/photos?kind=${kind}`, fd, {
                                headers: { "Content-Type": "multipart/form-data" },
                              });
                              setForm({ ...form, [field]: data[field] });
                              toast.success("Saved successfully");
                            } catch (err) {
                              toast.error(fmtErr(err));
                            }
                            e.target.value = "";
                          }}
                        />
                      </label>
                      {form[field] && (
                        <button
                          type="button"
                          className="text-xs font-bold px-3 py-1.5 rounded-md border border-rose-300 bg-rose-50 text-rose-800"
                          onClick={async () => {
                            if (!window.confirm("Delete this image?")) return;
                            try {
                              await api.delete(`/master/photos/${kind}/0`);
                              setForm({ ...form, [field]: null });
                              toast.success("Deleted");
                            } catch (err) {
                              toast.error(fmtErr(err));
                            }
                          }}
                        >
                          <Trash2 className="h-3 w-3 inline mr-1" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="sf-label">Stamp Mode</label>
                <select
                  className="sf-input"
                  value={form.pdf_stamp_mode || "none"}
                  onChange={(e) => setForm({ ...form, pdf_stamp_mode: e.target.value })}
                >
                  <option value="online">Online Stamp</option>
                  <option value="manual">Manual Stamp</option>
                  <option value="none">No Stamp</option>
                </select>
              </div>
              <div>
                <label className="sf-label">Signature Mode</label>
                <select
                  className="sf-input"
                  value={form.pdf_signature_mode || "none"}
                  onChange={(e) => setForm({ ...form, pdf_signature_mode: e.target.value })}
                >
                  <option value="online">Online Signature</option>
                  <option value="manual">Manual Signature</option>
                  <option value="none">No Signature</option>
                </select>
              </div>
            </div>
          </div>

          <button data-testid="save-settings-btn" className="sf-btn-primary" onClick={save} disabled={busy}>
            {busy ? "..." : t("saveSettings")}
          </button>
          <div className="sf-card p-4 text-sm text-slate-600 border border-slate-200" data-testid="sms-gateway-note">
            <div className="flex items-center gap-2 font-display font-bold text-slate-900 mb-1.5">
              <MessageSquareText className="h-4 w-4" /> SMS Gateway
            </div>
            OTPs, order and low-stock alerts are in <span className="font-semibold">demo mode</span> (shown on screen / stored in portal).
            Set <span className="font-mono-sf text-xs bg-slate-100 rounded px-1">SMS_MODE=live</span>,{" "}
            <span className="font-mono-sf text-xs bg-slate-100 rounded px-1">SMS_API_URL</span> and{" "}
            <span className="font-mono-sf text-xs bg-slate-100 rounded px-1">SMS_API_KEY</span> in the backend environment when your
            gateway is ready. Razorpay activates once <span className="font-mono-sf text-xs bg-slate-100 rounded px-1">RAZORPAY_KEY_ID/SECRET</span> are set.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="sf-card p-6" data-testid="add-admin-card">
            <div className="flex items-center gap-2 font-display font-bold text-slate-900 mb-4">
              <UserPlus className="h-4 w-4" /> {t("addAnotherAdmin")}
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <input data-testid="new-admin-name-input" className="sf-input" placeholder={t("adminNamePh")} value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} />
              <input data-testid="new-admin-mobile-input" className="sf-input" inputMode="numeric" placeholder={t("adminMobilePh")} value={newAdmin.mobile} onChange={(e) => setNewAdmin({ ...newAdmin, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
              <input data-testid="new-admin-pin-input" className="sf-input" inputMode="numeric" placeholder={t("pinPh")} value={newAdmin.pin} onChange={(e) => setNewAdmin({ ...newAdmin, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
              {user?.is_master && (
                <select
                  data-testid="new-admin-type-input"
                  className="sf-input"
                  value={newAdmin.admin_type}
                  onChange={(e) => setNewAdmin({ ...newAdmin, admin_type: e.target.value })}
                >
                  <option value="admin">Normal Admin</option>
                  <option value="stock_admin">Stock Admin</option>
                </select>
              )}
              <button data-testid="add-admin-btn" className="sf-btn-primary" onClick={addAdmin} disabled={busy}>
                <UserPlus className="h-4 w-4" /> {t("addAdmin")}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              The new admin logs in from the same portal with their mobile + OTP + this PIN. Share the PIN with them privately.
            </p>          </div>

          <div className="sf-card overflow-x-auto" data-testid="admins-table">
            <table className="w-full min-w-[620px]">
              <thead>
                <tr>
                  <th className="sf-th">{t("name")}</th>
                  <th className="sf-th">{t("mobileLbl")}</th>
                  <th className="sf-th">{t("role")}</th>
                  <th className="sf-th">{t("added")}</th>
                  <th className="sf-th text-right">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.id} className="sf-tr" data-testid={`admin-row-${a.mobile}`}>
                    <td className="sf-td font-semibold text-slate-900">{a.name}</td>
                    <td className="sf-td">+91 {a.mobile}</td>
                    <td className="sf-td">
                      {a.is_main ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#0B192C] text-white text-[10px] font-bold px-2.5 py-0.5 uppercase tracking-wider">
                          <ShieldCheck className="h-3 w-3" /> {t("mainAdmin")}
                        </span>
                      ) : a.admin_type === "stock_admin" ? (
                        <span className="rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 text-[10px] font-bold px-2.5 py-0.5 uppercase tracking-wider">
                          Stock Admin
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-[10px] font-bold px-2.5 py-0.5 uppercase tracking-wider">{t("adminRole")}</span>
                      )}
                      {a.mobile === myMobile && <span className="ml-1.5 text-xs text-slate-400">{t("you")}</span>}
                    </td>
                    <td className="sf-td">{fmtDate(a.created_at)}</td>
                    <td className="sf-td text-right">
                      <div className="inline-flex gap-2">
                        <button data-testid={`reset-pin-${a.mobile}`} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors" onClick={() => resetPin(a)}>
                          <KeyRound className="h-3 w-3" /> {t("resetPin")}
                        </button>
                        <button
                          data-testid={`remove-admin-${a.mobile}`}
                          className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={a.is_main || a.mobile === myMobile}
                          onClick={() => removeAdmin(a)}
                        >
                          <Trash2 className="h-3 w-3" /> {t("remove")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
